import { OptionsApi } from '@core/swagger-client';
import type { QueryClient } from '@tanstack/react-query';
import { useLocation } from '@tanstack/react-router';
import type { ParsedLocation } from '@tanstack/router-core';
import { defaultGetScrollRestorationKey } from '@tanstack/router-core';
import { useEffect } from 'react';

/** The pages a back link can return to. Links into a webinar page pass the one they are on as `state.backTo`. */
export type BackOrigin = 'overview' | 'content' | 'insights';

export type OverviewTab = 'upcoming' | 'ended';

declare module '@tanstack/history' {
    interface HistoryState {
        /** The page this one was opened from, for its back link. */
        backTo?: BackOrigin;
        /** Restore the scroll position saved under this key instead of the entry's own: a back link resumes an overview entry. */
        scrollKey?: string;
        /** The overview scrolls this webinar's card into view. */
        showWebinar?: string;
    }
}

/** The router's scroll positions are kept per key; by default one per history entry. */
export const getScrollRestorationKey = (location: ParsedLocation) => location.state.scrollKey ?? defaultGetScrollRestorationKey(location);

// The last page of each back origin: its path and the key its scroll position is saved under when you leave it. Per
// browser tab, and it survives a reload, like the router's own scroll positions.
type LastPage = { pathname: string; scrollKey: string };
const storageKey = (origin: BackOrigin) => `back-to-${origin}`;

/** For a page a back link can return to (`pathname`: the page's own path), so the back link restores its position. */
export const useRememberPage = (origin: BackOrigin, pathname: string) => {
    const location = useLocation();
    useEffect(() => {
        // Only while the page is the location: during a navigation away it is already the next page's.
        if (location.pathname !== pathname) return;
        try {
            sessionStorage.setItem(storageKey(origin), JSON.stringify({ pathname, scrollKey: getScrollRestorationKey(location) } satisfies LastPage));
        } catch {
            // No sessionStorage: back links go to the page, at the top.
        }
    }, [origin, pathname, location]);
};

const getLastPage = (origin: BackOrigin): LastPage | undefined => {
    try {
        const last = JSON.parse(sessionStorage.getItem(storageKey(origin)) ?? 'null');
        return typeof last?.pathname === 'string' && typeof last.scrollKey === 'string' ? last : undefined;
    } catch {
        return undefined;
    }
};

/** The link state that restores the scroll position of `pathname`, when it is the origin's last page. */
export const getScrollState = (origin: BackOrigin, pathname: string) => {
    const last = getLastPage(origin);
    return last?.pathname === pathname ? { scrollKey: last.scrollKey } : undefined;
};

// Overview entries whose webinar was shown: a refetch of the list, or coming back to the entry, does not scroll again.
const shownEntries = new Set<string>();

/** For the overview page: once the list is there, scroll the card of `state.showWebinar` into view (top when it is not in the list). */
export const useShowWebinar = (listLoaded: boolean) => {
    const location = useLocation();
    useEffect(() => {
        const { showWebinar, __TSR_key: key } = location.state;
        if (!showWebinar || !key || !listLoaded || shownEntries.has(key)) return;
        shownEntries.add(key);
        const card = document.querySelector(`[data-webinar-id="${CSS.escape(showWebinar)}"]`);
        if (card) card.scrollIntoView({ block: 'center' });
        else scrollTo({ top: 0 });
    }, [location, listLoaded]);
};

/** Back to the overview as you left it: the last tab, at its scroll position. Nothing remembered: upcoming, at the top. */
export const getOverviewTarget = () => {
    const tab: OverviewTab = getLastPage('overview')?.pathname === '/organized/ended' ? 'ended' : 'upcoming';
    return { to: '/organized/$tab', params: { tab }, state: getScrollState('overview', `/organized/${tab}`) } as const;
};

/**
 * Before going to the overview after its lists changed (a webinar deleted, a broadcast ended): without the cached
 * lists its loader fetches them before the page renders, so the scroll position is restored into the new list.
 */
export const dropOverviewLists = (queryClient: QueryClient) => {
    queryClient.removeQueries({ queryKey: OptionsApi.webinars.getMyUpcoming().queryKey, exact: true });
    queryClient.removeQueries({ queryKey: OptionsApi.webinars.getMyEnded().queryKey, exact: true });
};
